import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Project} from '../domain/types';
import {projectDeleteBlockedReason} from '../domain/projects';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';

import {currentProjectIdSelector, projectDataSelector} from '../store/selectors/projects.selectors';
import {
  fetchProjects,
  createProject,
  updateProjectThunk,
  deleteProjectThunk,
  setCurrentProjectThunk,
  persistProjectMetadata,
} from '../store/slices/projectsThunks';
import {toggleProjectFavorite} from '../store/slices/projectsSlice';
import {getCurrentProjectId} from '../lib/projectPreferences';

import ProjectsToolbar from './projects/ProjectsToolbar';
import ProjectsTable from './projects/ProjectsTable';
import ProjectFormModal, {ProjectSubmission} from './projects/ProjectFormModal';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';

/**
 * Projects: one row per project, and every action on a project lives in its row.
 *
 * The desktop-era page was a card grid with a create form that unfolded above it and pushed the
 * list off screen. This is the workspace's table instead — the same panel, search box and row
 * actions — because a project is a container, not something with contents worth previewing.
 */
const ProjectsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();

  const projects = useAppSelector(projectDataSelector);
  const currentProjectId = useAppSelector(currentProjectIdSelector);
  const isLoading = useAppSelector(state => state.projects.loading);

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchProjects())
      .unwrap()
      .then((loaded: Project[]) => {
        // Nothing selected yet: fall back to the project flagged as the default, and to the first
        // project if the backend has not flagged one. Landing here with no current project leaves
        // every project-scoped page without context.
        const fallback = loaded.find(project => project.isDefault) ?? loaded[0];
        if (!getCurrentProjectId() && fallback) {
          dispatch(setCurrentProjectThunk(fallback));
        }
      })
      .catch(err => setError(String(err)));
  }, [dispatch]);

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? projects.filter(project => project.name.toLowerCase().includes(query))
      : projects;
  }, [projects, search]);

  const editingProject = editingProjectId
    ? projects.find(project => project.id === editingProjectId) ?? null
    : null;

  const handleOpen = (project: Project) => {
    dispatch(setCurrentProjectThunk(project));
    navigate(`/projects/${project.id}/workspace`);
  };

  const handleSubmitForm = async (submission: ProjectSubmission) => {
    setIsSaving(true);
    setError(null);
    try {
      if (formMode === 'edit' && editingProject) {
        await dispatch(updateProjectThunk({
          id: editingProject.id,
          name: submission.name,
          description: submission.description,
        })).unwrap();
        showToast(`“${submission.name}” saved`, 'success');
        setFormMode(null);
        setEditingProjectId(null);
      } else {
        const project = await dispatch(createProject({
          name: submission.name,
          description: submission.description,
        })).unwrap();
        showToast(`“${project.name}” created`, 'success');
        setFormMode(null);
        setEditingProjectId(null);
        handleOpen(project);
      }
    } catch (err) {
      setError(String(err));
      showToast('Could not save that project', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteBlockedReason = (project: Project) => projectDeleteBlockedReason(project, projects);

  const handleConfirmDelete = async () => {
    if (!projectToDelete) {
      return;
    }

    // Re-check on confirm: the list can have shrunk to one — or the flags can have arrived from a
    // refetch — while the modal was open.
    const blockedReason = deleteBlockedReason(projectToDelete);
    if (blockedReason) {
      showToast(blockedReason, 'warning');
      setProjectToDelete(null);
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await dispatch(deleteProjectThunk(projectToDelete.id)).unwrap();
      showToast(`“${projectToDelete.name}” deleted`, 'success');
      setProjectToDelete(null);
    } catch (err) {
      setError(String(err));
      showToast('Could not delete that project', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleFavorite = (project: Project) => {
    // Favourites are still local preferences rather than a column on the project record, so the
    // optimistic toggle is the source of truth and persistence is fire-and-forget.
    dispatch(toggleProjectFavorite(project.id));
    dispatch(persistProjectMetadata({projectId: project.id, favorite: !project.favorite}));
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
        <ProjectsToolbar
          onCreate={() => {
            setEditingProjectId(null);
            setFormMode('create');
          }}
          error={error}
          onDismissError={() => setError(null)}
        />

        <ProjectsTable
          projects={filteredProjects}
          currentProjectId={currentProjectId}
          isLoading={isLoading}
          isBusy={isSaving || isDeleting}
          search={search}
          onSearchChange={setSearch}
          onCreate={() => {
            setEditingProjectId(null);
            setFormMode('create');
          }}
          onOpen={handleOpen}
          onEdit={(project) => {
            setEditingProjectId(project.id);
            setFormMode('edit');
          }}
          onDelete={setProjectToDelete}
          onToggleFavorite={handleToggleFavorite}
          deleteBlockedReason={deleteBlockedReason}
        />

        <p className="max-w-2xl px-1 text-xs text-gray-600">
          The Default Project is where anything you start without picking a project lands. It cannot
          be deleted, and neither can your last remaining project — but you can rename either one
          like any other.
        </p>
      </div>

      <ProjectFormModal
        isOpen={formMode !== null}
        mode={formMode ?? 'create'}
        project={formMode === 'edit' ? editingProject : null}
        isSaving={isSaving}
        onClose={() => {
          setFormMode(null);
          setEditingProjectId(null);
        }}
        onSubmit={handleSubmitForm}
      />

      <DeleteConfirmationModal
        isOpen={projectToDelete !== null}
        title="Delete project?"
        listName={projectToDelete?.name ?? null}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setProjectToDelete(null)}
        details={
          <>
            Its transcriptions and project-specific word lists go with it.
            {projectToDelete && projectToDelete.totalTranscriptions > 0 && (
              <span className="mt-1 block text-amber-300">
                {projectToDelete.totalTranscriptions} transcription
                {projectToDelete.totalTranscriptions === 1 ? '' : 's'} will be deleted.
              </span>
            )}
          </>
        }
      />
    </div>
  );
};

export default ProjectsContainer;
